/**
 * The Hair tool's viewport half — ADR-039 T1: the skull DRAWN on the head and
 * the region it finds TINTED on the flesh, with the one-drag confirm's handle.
 *
 * WHAT IT DRAWS. The skull is three great circles of its ellipsoid, declared in
 * BIND-WORLD and carried to where the head IS each frame through the Head
 * bone's skinning transform (mesh·bindMatrix⁻¹·head·boneInverse — the same
 * product the GPU applies to the flesh), so it rides a playing clip. The region
 * is a second zone-tint ghost (the zones panel keeps its own), painted with the
 * faces handed in. It decides nothing: the controller (app/src/hair) measures,
 * this draws what it is given.
 *
 * THE DRAG (Allen, 2026-09-30: "a one-drag confirm of the drawn skull"). A
 * round handle sits on the skull's centre while the skull is editable — the
 * elbow pin's form. Drag it to move the skull in the view plane; the wheel over
 * it scales the skull, Shift+wheel its height only; arrow keys nudge it, + / −
 * scale it. The mixer is HELD while a drag is in flight (the zone bar's rule:
 * moving flesh carries the target out from under the pointer). A finished
 * gesture reports the new skull ONCE (`adjust`) — the controller re-measures,
 * and nothing is recorded until Confirm.
 *
 * Depends on: three, zone-tint, the viewport's editing surface, kernel types.
 * Depended on by: main.
 */
import {
  BufferGeometry,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Plane,
  Quaternion,
  Raycaster,
  SkinnedMesh,
  Vector2,
  Vector3,
  type Object3D,
} from 'three';
import { isViewportDebris } from '@harryhausen/adapter-three';
import type { SkullDeclarationShape } from '@harryhausen/kernel';
import type { HairDisplay } from '../hair/hair-controller.js';
import type { Viewport } from './viewport.js';
import { createZoneTint } from './zone-tint.js';

/** Linear RGB — the hair hue (violet: unclaimed by tail amber, cloth cyan, soft-tissue rose). */
export const HAIR_HUE = [0.62, 0.45, 1] as const;
const SKULL_OFFER = 0xf2ecff;
const SKULL_CONFIRMED = 0xa98bff;
/** One wheel notch scales the skull by this much. */
const WHEEL_STEP = 1.04;
/** A semi-axis never leaves this band (metres) — a skull is not a pea, nor a house. */
const AXIS_MIN = 0.01;
const AXIS_MAX = 0.6;

export interface HairToolDeps {
  readonly viewport: Viewport;
  scene(): Object3D;
  /** Hold the mixer while a drag is in flight. */
  hold(on: boolean): void;
  /** A finished gesture: the skull a person placed (bind-world). */
  adjust(skull: SkullDeclarationShape): void;
}

export interface HairToolProbe {
  readonly on: boolean;
  readonly editable: boolean;
  readonly headBone: string | null;
  /** The skull's centre where it is drawn now (world). */
  readonly centreWorld: { x: number; y: number; z: number } | null;
  readonly semiAxes: { x: number; y: number; z: number } | null;
  readonly paintedFaces: number;
  readonly handle: { x: number; y: number } | null;
}

function soleSkinned(root: Object3D): SkinnedMesh | null {
  let found: SkinnedMesh | null = null;
  let count = 0;
  root.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh && !isViewportDebris(o)) {
      count++;
      found ??= o as SkinnedMesh;
    }
  });
  return count === 1 ? found : null;
}

/** Three great circles of the unit sphere, as line segments. */
function unitSkull(): BufferGeometry {
  const N = 72;
  const p: number[] = [];
  const ring = (f: (t: number) => [number, number, number]): void => {
    for (let k = 0; k < N; k++) p.push(...f((2 * Math.PI * k) / N), ...f((2 * Math.PI * (k + 1)) / N));
  };
  ring((t) => [Math.cos(t), Math.sin(t), 0]);
  ring((t) => [0, Math.sin(t), Math.cos(t)]);
  ring((t) => [Math.cos(t), 0, Math.sin(t)]);
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(p, 3));
  return g;
}

export function createHairTool(deps: HairToolDeps) {
  const surface = deps.viewport.editingSurface();
  const tint = createZoneTint(() => deps.scene());
  const material = new LineBasicMaterial({ color: SKULL_OFFER, transparent: true, opacity: 0.95, depthTest: false });
  const lines = new LineSegments(unitSkull(), material);
  // the editing surface's overlays never ride the subject, so the export door cannot meet these lines
  lines.renderOrder = 1001;
  lines.frustumCulled = false;
  lines.matrixAutoUpdate = false;
  lines.matrixWorldAutoUpdate = false;
  lines.visible = false;
  surface.overlays.add(lines);

  const style = document.createElement('style');
  style.textContent = `
    .hair-skull-handle {position:fixed;z-index:1200;width:26px;height:26px;padding:0;border:3px solid #efe7ffcc;border-radius:50%;background:#9d7bff55;box-shadow:0 0 0 4px #1c142b40,0 2px 9px #0004;cursor:grab;touch-action:none;transform:translate(-50%,-50%)}
    .hair-skull-handle:active {cursor:grabbing}
    .hair-skull-handle:focus-visible {outline:3px solid #fff;outline-offset:6px}
    .hair-skull-handle[hidden] {display:none}
  `;
  document.head.appendChild(style);
  const handle = document.createElement('button');
  handle.type = 'button';
  handle.className = 'hair-skull-handle';
  handle.hidden = true;
  handle.setAttribute('aria-label', 'Skull: drag to move, wheel to scale (Shift: height only), arrows to nudge, + and − to scale');
  handle.title = 'Drag to move the skull · wheel to scale (Shift: height only)';
  document.body.appendChild(handle);

  let shown: HairDisplay | null = null;
  /** The skull as drawn now (a drag's draft moves this before the controller hears of it). */
  let skull: SkullDeclarationShape | null = null;
  let paintedFaces = 0;
  let headBone: string | null = null;
  const B = new Matrix4();
  const S = new Matrix4();
  let bValid = false;

  /** Bind-world → world for the Head: mesh · bindMatrix⁻¹ · head · boneInverse (three's skinning product). */
  const headTransform = (): boolean => {
    const mesh = soleSkinned(deps.scene());
    if (mesh === null || headBone === null) return false;
    const i = mesh.skeleton.bones.findIndex((b) => b.name === headBone);
    if (i < 0) return false;
    const bone = mesh.skeleton.bones[i]!;
    B.copy(mesh.matrixWorld).multiply(mesh.bindMatrixInverse).multiply(bone.matrixWorld).multiply(mesh.skeleton.boneInverses[i]!);
    return true;
  };
  const skullMatrix = (s: SkullDeclarationShape): Matrix4 =>
    S.compose(
      new Vector3(s.centre.x, s.centre.y, s.centre.z),
      new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), (s.yawDeg * Math.PI) / 180),
      new Vector3(s.semiAxes.x, s.semiAxes.y, s.semiAxes.z),
    );
  const centreWorld = (): Vector3 | null => (skull === null || !bValid ? null : new Vector3(skull.centre.x, skull.centre.y, skull.centre.z).applyMatrix4(B));

  /** Per frame: carry the skull to the head, and the handle to the skull. */
  const frame = (): void => {
    if (shown === null || skull === null) return;
    bValid = headTransform();
    if (!bValid) {
      lines.visible = false;
      handle.hidden = true;
      return;
    }
    lines.matrixWorld.multiplyMatrices(B, skullMatrix(skull));
    lines.visible = true;
    const c = centreWorld();
    const at = shown.editable && c !== null ? deps.viewport.projectPoint(c) : null;
    handle.hidden = at === null;
    if (at !== null) {
      handle.style.left = `${at.x}px`;
      handle.style.top = `${at.y}px`;
    }
  };

  // ── the gestures ──
  let drag: { pointer: number; plane: Plane; from: Vector3; centre0: Vector3 } | null = null;
  const ray = new Raycaster();
  const hitOn = (event: PointerEvent, plane: Plane): Vector3 | null => {
    const rect = surface.canvas.getBoundingClientRect();
    ray.setFromCamera(new Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, (-(event.clientY - rect.top) / rect.height) * 2 + 1), surface.camera);
    return ray.ray.intersectPlane(plane, new Vector3());
  };
  const commit = (): void => {
    if (skull !== null) deps.adjust(skull);
  };
  const scaleBy = (fx: number, fy: number, fz: number): void => {
    if (skull === null) return;
    const clamp = (x: number): number => Math.min(AXIS_MAX, Math.max(AXIS_MIN, x));
    skull = { ...skull, semiAxes: { x: clamp(skull.semiAxes.x * fx), y: clamp(skull.semiAxes.y * fy), z: clamp(skull.semiAxes.z * fz) } };
    frame();
  };
  /** Move the skull in its own head frame (u = left, v = up, w = forward), bind-world metres. */
  const nudge = (du: number, dv: number, dw: number): void => {
    if (skull === null) return;
    const y = (skull.yawDeg * Math.PI) / 180;
    const left = { x: Math.cos(y), z: -Math.sin(y) };
    const fwd = { x: Math.sin(y), z: Math.cos(y) };
    skull = { ...skull, centre: { x: skull.centre.x + du * left.x + dw * fwd.x, y: skull.centre.y + dv, z: skull.centre.z + du * left.z + dw * fwd.z } };
    frame();
  };
  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || shown === null || !shown.editable) return;
    const c = centreWorld();
    if (c === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const normal = surface.camera.getWorldDirection(new Vector3());
    const plane = new Plane().setFromNormalAndCoplanarPoint(normal, c);
    const from = hitOn(event, plane);
    if (from === null) return;
    drag = { pointer: event.pointerId, plane, from, centre0: c };
    deps.hold(true);
    surface.navigation(false);
    handle.setPointerCapture(event.pointerId);
  });
  handle.addEventListener('pointermove', (event) => {
    if (drag === null || event.pointerId !== drag.pointer || skull === null || !bValid) return;
    const hit = hitOn(event, drag.plane);
    if (hit === null) return;
    // the new world centre, carried back to bind-world through the head's inverse
    const world = drag.centre0.clone().add(hit.sub(drag.from));
    const bind = world.applyMatrix4(B.clone().invert());
    skull = { ...skull, centre: { x: bind.x, y: bind.y, z: bind.z } };
    frame();
  });
  const release = (event: PointerEvent): void => {
    if (drag === null || event.pointerId !== drag.pointer) return;
    drag = null;
    if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
    surface.navigation(true);
    deps.hold(false);
    commit();
  };
  handle.addEventListener('pointerup', release);
  handle.addEventListener('pointercancel', release);
  let wheelTimer: ReturnType<typeof setTimeout> | null = null;
  handle.addEventListener(
    'wheel',
    (event) => {
      if (shown === null || !shown.editable) return;
      event.preventDefault();
      // Shift+wheel arrives as a horizontal scroll on some platforms
      const d = event.deltaY !== 0 ? event.deltaY : event.deltaX;
      if (d === 0) return;
      const f = d < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      if (event.shiftKey) scaleBy(1, f, 1);
      else scaleBy(f, f, f);
      if (wheelTimer !== null) clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        wheelTimer = null;
        commit();
      }, 300);
    },
    { passive: false },
  );
  handle.addEventListener('keydown', (event) => {
    if (shown === null || !shown.editable || skull === null) return;
    const step = 0.002;
    const moves: Record<string, [number, number, number]> = {
      ArrowLeft: [step, 0, 0],
      ArrowRight: [-step, 0, 0],
      ArrowUp: [0, step, 0],
      ArrowDown: [0, -step, 0],
    };
    const m = moves[event.key];
    if (m !== undefined) nudge(...(event.shiftKey ? ([0, 0, m[1] !== 0 ? m[1] : 0] as [number, number, number]) : m));
    else if (event.key === '+' || event.key === '=') scaleBy(WHEEL_STEP, WHEEL_STEP, WHEEL_STEP);
    else if (event.key === '-' || event.key === '_') scaleBy(1 / WHEEL_STEP, 1 / WHEEL_STEP, 1 / WHEEL_STEP);
    else return;
    event.preventDefault();
    commit();
  });

  return {
    /** Land a wheel gesture still inside its debounce, so a Confirm right after it records the skull the person sees. */
    flush(): void {
      if (wheelTimer === null) return;
      clearTimeout(wheelTimer);
      wheelTimer = null;
      commit();
    },
    /** Draw this (or nothing). A refusal from the tint (faces of another mesh) is returned, never swallowed. */
    show(display: HairDisplay | null): string | null {
      shown = display;
      if (display === null) {
        skull = null;
        headBone = null;
        lines.visible = false;
        handle.hidden = true;
        tint.hide();
        paintedFaces = 0;
        return null;
      }
      // a drag in flight owns the skull until it lands
      if (drag === null && wheelTimer === null) skull = display.skull;
      headBone = display.headBone;
      material.color.setHex(display.confirmed && !display.editable ? SKULL_CONFIRMED : SKULL_OFFER);
      frame();
      const refusal = display.faces.length > 0 ? tint.show([{ id: 'hair', faces: display.faces, color: HAIR_HUE }]) : (tint.hide(), null);
      paintedFaces = refusal === null ? tint.probe().paintedFaces : 0;
      return refusal;
    },
    frame,
    probe(): HairToolProbe {
      const c = centreWorld();
      const at = c === null ? null : deps.viewport.projectPoint(c);
      return {
        on: shown !== null && lines.visible,
        editable: shown?.editable ?? false,
        headBone,
        centreWorld: c === null ? null : { x: c.x, y: c.y, z: c.z },
        semiAxes: skull?.semiAxes ?? null,
        paintedFaces,
        handle: handle.hidden ? null : at,
      };
    },
    /** Test and smoke door: the same gesture end a drag reaches, without a pointer. */
    adjustBy(du: number, dv: number, dw: number, scale = 1): void {
      nudge(du, dv, dw);
      if (scale !== 1) scaleBy(scale, scale, scale);
      commit();
    },
  };
}
export type HairTool = ReturnType<typeof createHairTool>;
